// src/components/workspace/investigation/IncidentTimeline.js

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCheck,
  CheckCircle2,
  Circle,
  ClipboardList,
  CreditCard,
  FileWarning,
  ListChecks,
  MessageSquareReply,
  Microscope,
  MonitorUp,
  Paperclip,
  PlayCircle,
  RotateCcw,
  ShieldCheck,
  Trash2,
  Undo2,
  UserCheck,
  Video,
  VideoOff,
} from "lucide-react";
import {
  EVENT_CATEGORY,
  EVENT_VISIBILITY,
  describeEvent,
} from "../../../services/incidentEventService";
import { formatClockTime, formatDateHeading } from "../../../utils/duration";

/**
 * The operational record of the incident.
 *
 * This is the default view of the workspace because "what has happened so far"
 * is the first question both parties have, and reconstructing it by scrolling
 * chat is exactly what makes a support tool feel amateur.
 */

const ICONS = {
  AlertTriangle,
  CheckCheck,
  CheckCircle2,
  Circle,
  ClipboardList,
  CreditCard,
  FileWarning,
  ListChecks,
  MessageSquareReply,
  Microscope,
  MonitorUp,
  Paperclip,
  PlayCircle,
  RotateCcw,
  ShieldCheck,
  Trash2,
  Undo2,
  UserCheck,
  Video,
  VideoOff,
};

const CATEGORY_TONE = {
  [EVENT_CATEGORY.LIFECYCLE]: "bg-slate-100 text-slate-600",
  [EVENT_CATEGORY.COMMS]: "bg-sky-100 text-sky-700",
  [EVENT_CATEGORY.SESSION]: "bg-teal-100 text-teal-700",
  [EVENT_CATEGORY.EVIDENCE]: "bg-cyan-100 text-cyan-700",
  [EVENT_CATEGORY.INVESTIGATION]: "bg-blue-100 text-blue-700",
  [EVENT_CATEGORY.RESOLUTION]: "bg-emerald-100 text-emerald-700",
};

const FILTERS = [
  { id: "all", label: "All activity" },
  { id: EVENT_CATEGORY.INVESTIGATION, label: "Investigation" },
  { id: EVENT_CATEGORY.SESSION, label: "Sessions" },
  { id: EVENT_CATEGORY.EVIDENCE, label: "Evidence" },
  { id: EVENT_CATEGORY.RESOLUTION, label: "Resolution" },
];

function EventRow({ event }) {
  const definition = describeEvent(event);
  const Icon = ICONS[definition.icon] || Circle;
  const tone =
    CATEGORY_TONE[event.category] || CATEGORY_TONE[EVENT_CATEGORY.LIFECYCLE];

  return (
    <li className="relative flex gap-3 pl-1">
      {/* Connector rail */}
      <span
        className="absolute left-5 top-9 bottom-0 w-px bg-slate-200"
        aria-hidden="true"
      />
      <span
        className={`relative z-10 mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ring-4 ring-white ${tone}`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1 pb-5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p className="text-sm font-medium text-slate-900">
            {definition.summary}
          </p>
          <time className="font-mono text-xs text-slate-400">
            {formatClockTime(event.createdAt)}
          </time>
          {event.visibility === EVENT_VISIBILITY.INTERNAL && (
            <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide text-slate-500">
              Internal
            </span>
          )}
        </div>

        {event.actorName && (
          <p className="mt-0.5 text-xs text-slate-500">{event.actorName}</p>
        )}

        {event.detail && (
          <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-600">
            {event.detail}
          </p>
        )}
      </div>
    </li>
  );
}

export default function IncidentTimeline({ events = [], viewerRole }) {
  const [filter, setFilter] = useState("all");

  const visibleEvents = useMemo(() => {
    // Internal working notes are part of the audit trail but are not the
    // customer's business.
    const scoped =
      viewerRole === "client"
        ? events.filter((e) => e.visibility !== EVENT_VISIBILITY.INTERNAL)
        : events;

    return filter === "all"
      ? scoped
      : scoped.filter((e) => e.category === filter);
  }, [events, filter, viewerRole]);

  const groups = useMemo(() => {
    const byDay = new Map();
    for (const event of visibleEvents) {
      const heading = formatDateHeading(event.createdAt) || "Pending";
      if (!byDay.has(heading)) byDay.set(heading, []);
      byDay.get(heading).push(event);
    }
    return Array.from(byDay.entries());
  }, [visibleEvents]);

  return (
    <section className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-4 py-2.5">
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setFilter(option.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              filter === option.id
                ? "bg-slate-800 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {groups.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="max-w-sm text-center text-sm text-slate-500">
              No activity recorded yet. Every action taken on this incident —
              sessions, evidence, findings, and the resolution — is logged here
              as it happens.
            </p>
          </div>
        ) : (
          groups.map(([heading, dayEvents]) => (
            <div key={heading} className="mb-2">
              <p className="sticky top-0 z-10 -mx-4 mb-3 bg-white px-4 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                {heading}
              </p>
              <ul className="list-none">
                {dayEvents.map((event) => (
                  <EventRow key={event.id} event={event} />
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
